import {Delete} from '@mui/icons-material';
import {useCanAny, useCan} from 'src/permissions/hooks';
import {IconButton} from '@mui/material';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {companyApi, locationsApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {ConformationModal} from 'src/components/common/ConformationModal';
import {LocationSortFieldEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, LocationDTO} from 'src/types';
import {NotifyError} from 'src/utils/notification';
import theme from 'src/theme';

type GetLocationTrigger = ReturnType<typeof locationsApi.useGetLocationsQuery>[0];
type DeleteLocationMutationTrigger = ReturnType<typeof locationsApi.useDeleteLocationMutation>[0];
type DeleteLocationFromCompanyMutationTrigger = ReturnType<
    typeof companyApi.useRemoveLocationFromCompanyMutation
>[0];

const handleDeleteLocation =
    (
        trigger: DeleteLocationMutationTrigger,
        companyTrigger: DeleteLocationFromCompanyMutationTrigger,
        dispatch: AppDispatch,
        locationId: number,
        businessId: number,
        isCompanyUser: boolean,
        companyId: number,
        refetch: GetLocationTrigger,
        handleClose: () => void
    ) =>
        async () => {
            dispatch(preloaderChangeStatus(true));
            try {
                if (isCompanyUser && !companyId) {
                    await companyTrigger({
                        companyId,
                        data: {
                            locationId,
                            businessId,
                        },
                    }).unwrap();

                    refetch();

                    return;
                }

                await trigger({locationId, businessId}).unwrap();
            } catch (error: any) {
                NotifyError(error.data.message);
            } finally {
                handleClose();
                dispatch(preloaderChangeStatus(false));
            }
        };

export const DeleteLocation: FC<{ row: LocationDTO; isCompanyUser?: boolean }> = ({
                                                                                      row,
                                                                                      isCompanyUser = false,
                                                                                  }) => {
    // Deleting the location, or detaching it from a company - the caller's reach decides which this
    // control performs.
    const canDelete = useCanAny('locations.delete', 'companies.assignLocations');
    const {authInfo} = useAppSelector((state) => state.common);
    const companyId = authInfo?.company?.id || authInfo?.employee?.company?.id || row?.company?.id;

    const {refetch} = locationsApi.useGetLocationsQuery({
        params: {
            page: 0,
            size: 100,
        },
        data: {
            businessId: row.business.id,
            companyId: companyId || undefined,
            sortAsc: true,
            sortField: LocationSortFieldEnum.ID,
        },
    });
    const [trigger] = locationsApi.useDeleteLocationMutation();
    const [companyTrigger] = companyApi.useRemoveLocationFromCompanyMutation();
    const dispatch = useAppDispatch();

    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'deleteLocation'});

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };
    if (!canDelete) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={{
                    width: '28px',
                    height: '28px',
                    p: '2px',
                    color: theme.palette.neutral[400],
                    backgroundColor: 'transparent',
                    '.MuiSvgIcon-root': {
                        fill: theme.palette.neutral[400],
                        width: '20px',
                    },
                }}
                onClick={handleOpenModal}
            >
                <Delete/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ConformationModal
                    handleClose={handleCloseModal}
                    handleSubmit={handleDeleteLocation(
                        trigger,
                        companyTrigger,
                        dispatch,
                        +row.id,
                        +row.business.id,
                        isCompanyUser,
                        companyId ?? 0,
                        refetch,
                        handleCloseModal
                    )}
                    text={'deletionLocation'}
                />
            </AMPModal>
        </>
    );
};
